import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import useScheduleStore, { BlockBar } from '../stores/useScheduleStore';
import { ScheduleEvent, EventColorEntry, OOOEntry } from './types';
import { UNAVAILABLE_STYLE } from './schedule-colors';
import { buttonClass } from '../components/ui/Button';
import { Popover } from '../components/ui/Popover';
import { useThemeStore } from '../stores/useThemeStore';
import Tooltip from '../components/ui/Tooltip';

interface CellAssignmentPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
  dateStr: string;
  eventId?: string;
  eventsOnDay: ScheduleEvent[];
  userAssignments: { event_id: string; role: string }[];
  eventColorMap: Map<string, EventColorEntry>;
  oooEntries: OOOEntry[];
  /** Unavailability block on this (user, date), if any — drives Mark vs Remove UI. */
  blockBar?: BlockBar | null;
  onEditOOO: (entry: OOOEntry) => void;
  position: { top: number; left: number };
}

const CellAssignmentPopover: React.FC<CellAssignmentPopoverProps> = ({
  isOpen, onClose, userId, userName, dateStr, eventsOnDay,
  userAssignments, eventColorMap, oooEntries, blockBar, onEditOOO, position,
}) => {
  const dayMode = useThemeStore((st) => st.dayMode);
  const addPendingChange = useScheduleStore(s => s.addPendingChange);
  const addPendingBlockChange = useScheduleStore(s => s.addPendingBlockChange);
  const [markingBlock, setMarkingBlock] = useState(false);
  const [blockNotes, setBlockNotes] = useState('');

  const assignedEventIds = new Set(userAssignments.map(a => a.event_id));
  const unassignedEvents = eventsOnDay.filter(e => !assignedEventIds.has(e.id));

  const handleAssign = (eventId: string, eventName: string) => {
    addPendingChange({
      id: `add-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      action: 'add',
      event_id: eventId,
      user_id: userId,
      date: dateStr,
      event_name: eventName,
      user_name: userName,
    });
    onClose();
  };

  const handleUnassign = (eventId: string, eventName: string) => {
    addPendingChange({
      id: `rm-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      action: 'remove',
      event_id: eventId,
      user_id: userId,
      date: dateStr,
      event_name: eventName,
      user_name: userName,
    });
    onClose();
  };

  const handleConfirmMarkUnavailable = () => {
    addPendingBlockChange({
      id: `block-popover-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      action: 'add',
      user_id: userId,
      date: dateStr,
      notes: blockNotes.trim(),
    });
    setMarkingBlock(false);
    setBlockNotes('');
    onClose();
  };

  const handleRemoveBlock = () => {
    if (!blockBar?.id) return;
    addPendingBlockChange({
      id: `block-rm-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      action: 'remove',
      user_id: userId,
      date: blockBar.blockStart,
      blockId: blockBar.id,
    });
    onClose();
  };

  const formatDate = (ds: string) => {
    const d = new Date(ds + 'T12:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <Popover
      open={isOpen}
      onClose={onClose}
      anchor={{ x: position.left, y: position.top }}
      title={userName}
      meta={formatDate(dateStr)}
      width="sm"
      light={dayMode}
    >
      {/* Portalled out of the page's day-mode wrapper, so the content carries it itself. */}
      <div className={dayMode ? 'day-mode' : undefined}>
        <div className="space-y-1">
          {/* OOO entries */}
          {oooEntries.length > 0 && (
            <div className="px-1 py-1">
              {oooEntries.map(o => (
                <button
                  key={o.id}
                  onClick={() => { onEditOOO(o); onClose(); }}
                  className="w-full text-left px-2 py-1 rounded text-xs text-on-surface day:text-white
                    bg-surface day:bg-gray-900 hover:bg-surface-container-high day:hover:bg-gray-800 transition-colors"
                >
                  OOO{o.is_tentative ? '?' : ''} — {o.notes || 'No notes'}
                  <span className="ml-1 text-on-surface-variant day:text-gray-300">
                    {o.entered_in_onpay ? '(OnPay)' : ''}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Current assignments */}
          {userAssignments.length > 0 && (
            <>
              <div className="px-1 text-[10px] text-on-surface-variant day:text-gray-600 uppercase tracking-wider">Assigned</div>
              {userAssignments.map(a => {
                const color = eventColorMap.get(a.event_id);
                const evt = eventsOnDay.find(e => e.id === a.event_id);
                return (
                  <div
                    key={a.event_id}
                    className="px-1 py-1.5 rounded hover:bg-surface-container-high day:hover:bg-gray-100"
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-medium ${color?.text || 'text-on-surface day:text-gray-700'}`}>
                        {evt?.name || '?'} {a.role && <span className="opacity-60">({a.role})</span>}
                      </span>
                      <button
                        onClick={() => handleUnassign(a.event_id, evt?.name || '')}
                        className={buttonClass('danger-quiet', 'sm')}
                      >
                        Remove
                      </button>
                    </div>
                    <Link
                      to={`/events/${a.event_id}`}
                      onClick={onClose}
                      className="text-[10px] text-on-surface-variant day:text-gray-600 hover:text-primary day:hover:text-amber-700 transition-colors mt-0.5 inline-block"
                    >
                      View Event &rarr;
                    </Link>
                  </div>
                );
              })}
            </>
          )}

          {/* Available events to assign */}
          {unassignedEvents.length > 0 && !oooEntries.length && (
            <>
              <div className="px-1 text-[10px] text-on-surface-variant day:text-gray-600 uppercase tracking-wider pt-1">
                Assign to
              </div>
              {unassignedEvents.map(evt => {
                const color = eventColorMap.get(evt.id);
                return (
                  <div key={evt.id} className="flex items-center gap-1">
                    <button
                      onClick={() => handleAssign(evt.id, evt.name)}
                      className={`flex-1 text-left px-2 py-1 rounded text-xs font-medium
                        ${color?.bg || 'bg-gray-700/30 day:bg-gray-200'} ${color?.text || 'text-on-surface day:text-gray-700'}
                        hover:opacity-80 transition-opacity`}
                    >
                      {evt.name}
                      <span className="opacity-50 ml-1">
                        ({evt.ls_drone_count + evt.pyro_drone_count})
                      </span>
                    </button>
                    <Link
                      to={`/events/${evt.id}`}
                      onClick={onClose}
                      className="text-[10px] text-on-surface-disabled day:text-gray-500 hover:text-primary day:hover:text-amber-700 transition-colors px-1 shrink-0"
                      title="View event"
                    >
                      &rarr;
                    </Link>
                  </div>
                );
              })}
            </>
          )}

          {/* Unavailability block — show existing one, or offer to mark.
              Mutually exclusive with OOO (OOO takes priority visually). */}
          {oooEntries.length === 0 && blockBar && !blockBar.isPendingAdd && (
            <div className="px-1 pt-1">
              <div className="text-[10px] text-on-surface-variant day:text-gray-600 uppercase tracking-wider mb-1">
                Unavailable
              </div>
              <div className={`px-2 py-1.5 rounded border text-xs flex items-start justify-between gap-2
                ${UNAVAILABLE_STYLE.bg} ${UNAVAILABLE_STYLE.text} ${UNAVAILABLE_STYLE.border}`}>
                <div className="flex-1 min-w-0">
                  <div className="truncate font-medium">{blockBar.notes || 'No reason given'}</div>
                  <div className="text-[10px] opacity-70 mt-0.5">
                    {blockBar.blockStart === blockBar.blockEnd
                      ? formatDate(blockBar.blockStart)
                      : `${formatDate(blockBar.blockStart)} – ${formatDate(blockBar.blockEnd)}`}
                  </div>
                </div>
                <button
                  onClick={handleRemoveBlock}
                  className={`${buttonClass('danger-quiet', 'sm')} shrink-0`}
                >
                  Remove
                </button>
              </div>
            </div>
          )}

          {/* Mark unavailable affordance — only when no existing block + no OOO */}
          {oooEntries.length === 0 && !blockBar && (
            <div className="px-1 pt-1">
              {!markingBlock ? (
                <Tooltip label="Mark this person as unavailable on this day"><button
                  onClick={() => setMarkingBlock(true)}
                  className={`${buttonClass('quiet', 'sm')} w-full`}
                >
                  Mark unavailable…
                </button></Tooltip>
              ) : (
                <div className={`px-2 py-1.5 rounded border space-y-1.5
                  ${UNAVAILABLE_STYLE.bg} ${UNAVAILABLE_STYLE.text} ${UNAVAILABLE_STYLE.border}`}>
                  <textarea
                    value={blockNotes}
                    onChange={(e) => setBlockNotes(e.target.value)}
                    placeholder="Optional note (e.g. cannot travel)"
                    rows={2}
                    autoFocus
                    className="w-full text-xs px-1.5 py-1 rounded bg-gray-900/30 day:bg-white/80 border border-slate-500/40 day:border-slate-300
                      text-on-surface day:text-slate-800 placeholder:text-on-surface-variant day:placeholder:text-slate-400 outline-none focus:ring-1 focus:ring-slate-400"
                  />
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => { setMarkingBlock(false); setBlockNotes(''); }}
                      className={buttonClass('quiet', 'sm')}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleConfirmMarkUnavailable}
                      className={buttonClass('tonal', 'sm')}
                    >
                      Add block
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* No events */}
          {eventsOnDay.length === 0 && oooEntries.length === 0 && !blockBar && (
            <div className="px-1 py-2 text-xs text-on-surface-variant day:text-gray-600 text-center">
              No events on this day
            </div>
          )}
        </div>
      </div>
    </Popover>
  );
};

export default CellAssignmentPopover;
